import {
    WORKING_HOURS_START_KEY_PREFIX,
    WORKING_HOURS_END_KEY_PREFIX,
    COLUMN_WIDTH_KEY_PREFIX,
    DOCTOR_FILTER_KEY_PREFIX,
    SELECTED_CLINIC_KEY_PREFIX,
    SHEPHERD_EMAIL_KEY_PREFIX,
    MOBILE_BROWSER_RECORDING_ACK_KEY_PREFIX,
    MIC_CHOICE_KEY_PREFIX,
} from "@common/lib/accountScopedStorageKeyPrefixes";

// Keys deliberately scoped per clinic account so they survive across logins on a shared
// workstation. Imported from the shared registry in accountScopedStorageKeyPrefixes.ts (the
// same constants each storage helper itself imports to build its keys), not duplicated as
// separate literals here, so this list can't silently drift out of sync with the real keys
// those helpers read and write. A blanket localStorage.clear() on sign-out defeats the point
// of that scoping. Add a new prefix to that registry when a new account-scoped storage helper
// is introduced.
const ACCOUNT_SCOPED_KEY_PREFIXES = [
    WORKING_HOURS_START_KEY_PREFIX,
    WORKING_HOURS_END_KEY_PREFIX,
    COLUMN_WIDTH_KEY_PREFIX,
    DOCTOR_FILTER_KEY_PREFIX,
    SELECTED_CLINIC_KEY_PREFIX,
    SHEPHERD_EMAIL_KEY_PREFIX,
    MOBILE_BROWSER_RECORDING_ACK_KEY_PREFIX,
    // Device-wide (not user-keyed): the mic belongs to the workstation, not the signed-in user.
    MIC_CHOICE_KEY_PREFIX,
];

// Clears all localStorage except keys matching one of the account-scoped prefixes above.
// Collects keys first, then removes them in a second pass — removing while iterating by
// index would shift subsequent indices and skip keys.
export function clearLocalStorageExceptAccountScopedPreferences(): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && !ACCOUNT_SCOPED_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))) {
            keysToRemove.push(key);
        }
    }
    // Per-key try/catch: one blocked/throwing removeItem (e.g. a browser extension
    // policy) must not abort removal of the rest, unlike the single localStorage.clear()
    // call this replaces, which would fail all-or-nothing.
    for (const key of keysToRemove) {
        try {
            localStorage.removeItem(key);
        } catch {
            // Ignore and continue clearing the remaining keys.
        }
    }
}
